import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  BarChart3, 
  Users, 
  Award,
  ArrowRight,
  Play,
  CheckCircle,
  Star,
  Globe,
  Smartphone,
  Monitor,
  Phone,
  Mail,
  MapPin,
  Send
} from 'lucide-react';

const Index = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  // Dynamic button URLs from admin dashboard
  const [buttonUrls, setButtonUrls] = useState({
    heroPrimary: '#contact',
    heroSecondary: '#services',
    navCta: '#contact',
    contactForm: 'mailto:info@reesha.agency',
    phoneLink: 'tel:+966501234567',
    emailLink: 'mailto:info@reesha.agency'
  });
  
  // Load button URLs from localStorage
  useEffect(() => {
    const heroPrimary = localStorage.getItem('hero-primary-cta-url') || '#contact';
    const heroSecondary = localStorage.getItem('hero-secondary-cta-url') || '#services';
    const navCta = localStorage.getItem('nav-cta-url') || '#contact';
    const contactForm = localStorage.getItem('contact-form-submit-url') || 'mailto:info@reesha.agency';
    const phoneLink = localStorage.getItem('phone-link-url') || 'tel:+966501234567';
    const emailLink = localStorage.getItem('email-link-url') || 'mailto:info@reesha.agency';
    
    setButtonUrls({
      heroPrimary,
      heroSecondary,
      navCta,
      contactForm,
      phoneLink,
      emailLink
    });
  }, []);
  
  // Dynamic page content from page editor
  const [pageContent, setPageContent] = useState(null);
  
  // Load page content from localStorage
  useEffect(() => {
    const savedContent = localStorage.getItem('website-content');
    if (savedContent) {
      setPageContent(JSON.parse(savedContent));
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({ name: '', email: '', message: '' });
    alert(t('success.saved'));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <Navigation />
      
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 lg:pt-28">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-background to-purple-900/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.1)_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <Badge variant="outline" className="mb-6 px-4 py-2 text-sm border-white/20 bg-white/5 backdrop-blur-sm">
              <span className="mr-2">🚀</span>
              {pageContent?.hero?.badge || t('hero.badge')}
            </Badge>
            
            {/* Main Title */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                {pageContent?.hero?.title || t('hero.title')}
              </span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              {pageContent?.hero?.subtitle || t('hero.subtitle')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="btn-glow bg-white text-black hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
                onClick={() => {
                  if (buttonUrls.heroPrimary.startsWith('#')) {
                    document.querySelector(buttonUrls.heroPrimary)?.scrollIntoView({ behavior: 'smooth' });
                  } else if (buttonUrls.heroPrimary.startsWith('mailto:') || buttonUrls.heroPrimary.startsWith('tel:')) {
                    window.location.href = buttonUrls.heroPrimary;
                  } else {
                    window.open(buttonUrls.heroPrimary, '_blank');
                  }
                }}
              >
                {pageContent?.hero?.primaryCTA || t('hero.cta.primary')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10 px-8 py-4 text-lg"
                onClick={() => {
                  if (buttonUrls.heroSecondary.startsWith('#')) {
                    document.querySelector(buttonUrls.heroSecondary)?.scrollIntoView({ behavior: 'smooth' });
                  } else if (buttonUrls.heroSecondary.startsWith('mailto:') || buttonUrls.heroSecondary.startsWith('tel:')) {
                    window.location.href = buttonUrls.heroSecondary;
                  } else {
                    window.open(buttonUrls.heroSecondary, '_blank');
                  }
                }}
              >
                <Play className="mr-2 h-5 w-5" />
                {pageContent?.hero?.secondaryCTA || t('hero.cta.secondary')}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="absolute bottom-20 left-0 right-0">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">300%</div>
                <div className="text-sm text-muted-foreground">{t('stats.roas')}</div>
              </div>
              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">50+</div>
                <div className="text-sm text-muted-foreground">{t('stats.clients')}</div>
              </div>
              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">2M+</div>
                <div className="text-sm text-muted-foreground">{t('stats.views')}</div>
              </div>
              <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border">
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">24/7</div>
                <div className="text-sm text-muted-foreground">{t('stats.support')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-background to-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {pageContent?.services?.title || t('services.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {pageContent?.services?.subtitle || t('services.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Performance Marketing */}
            <Card className="bg-card/50 border-border hover:border-blue-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
                  <Target className="w-8 h-8 text-blue-400" />
                </div>
                <CardTitle className="text-foreground text-xl">
                  {pageContent?.services?.performance?.title || t('services.performance.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {pageContent?.services?.performance?.description || t('services.performance.desc')}
                </p>
              </CardContent>
            </Card>

            {/* Growth Marketing */}
            <Card className="bg-card/50 border-border hover:border-green-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-16 h-16 bg-green-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-500/30 transition-colors">
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
                <CardTitle className="text-foreground text-xl">
                  {pageContent?.services?.growth?.title || t('services.growth.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {pageContent?.services?.growth?.description || t('services.growth.desc')}
                </p>
              </CardContent>
            </Card>

            {/* AI Video Production */}
            <Card className="bg-card/50 border-border hover:border-purple-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-16 h-16 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
                  <Zap className="w-8 h-8 text-purple-400" />
                </div>
                <CardTitle className="text-foreground text-xl">
                  {pageContent?.services?.aiVideo?.title || t('services.ai-video.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {pageContent?.services?.aiVideo?.description || t('services.ai-video.desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {pageContent?.process?.title || t('why-us.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {pageContent?.process?.subtitle || t('why-us.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              {(pageContent?.process?.features || [
                t('why-us.expert-team'),
                t('why-us.measurable-results'),
                t('why-us.advanced-tech'),
                t('why-us.support')
              ]).map((featureText, index) => {
                const icons = [Users, BarChart3, Zap, Award];
                const FeatureIcon = icons[index] || Users;
                return (
                <div key={index} className="flex items-center gap-4 p-4 bg-card/30 rounded-lg border border-border">
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <FeatureIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <span className="text-lg text-foreground">{featureText}</span>
                </div>
              );
              })}
            </div>

            {/* Testimonial */}
            <div className="bg-gradient-to-br from-card/50 to-muted/50 p-8 rounded-2xl border border-border">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
                <span className="text-muted-foreground ml-2">{pageContent?.process?.testimonial?.rating || t('why-us.rating')}</span>
              </div>
              <blockquote className="text-lg text-foreground mb-6 italic">
                "{pageContent?.process?.testimonial?.text || t('why-us.testimonial')}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">أ</span>
                </div>
                <div>
                  <div className="font-semibold text-foreground">{pageContent?.process?.testimonial?.author || 'أحمد محمد'}</div>
                  <div className="text-muted-foreground text-sm">{pageContent?.process?.testimonial?.position || 'مؤسس شركة تقنية'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-background to-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {pageContent?.contact?.title || t('contact.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {pageContent?.contact?.subtitle || t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="flex items-center gap-4 p-6 bg-card/30 rounded-lg border border-border">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">{pageContent?.contact?.formLabels?.phone || t('contact.phone')}</div>
                  <a href={buttonUrls.phoneLink} className="text-muted-foreground hover:text-foreground transition-colors">
                    {buttonUrls.phoneLink.replace('tel:', '')}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-6 bg-card/30 rounded-lg border border-border">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">{pageContent?.contact?.formLabels?.email || t('contact.email')}</div>
                  <a href={buttonUrls.emailLink} className="text-muted-foreground hover:text-foreground transition-colors">
                    {buttonUrls.emailLink.replace('mailto:', '')}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-6 bg-card/30 rounded-lg border border-border">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">{t('contact.address')}</div>
                  <div className="text-muted-foreground">
                    {pageContent?.contact?.address || 'الرياض، المملكة العربية السعودية'}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-card/30 p-8 rounded-2xl border border-border">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-foreground font-medium mb-2">
                    {pageContent?.contact?.formLabels?.name || t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={pageContent?.contact?.formLabels?.name || t('contact.form.name')}
                  />
                </div>

                <div>
                  <label className="block text-foreground font-medium mb-2">
                    {pageContent?.contact?.formLabels?.email || t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={pageContent?.contact?.formLabels?.email || t('contact.form.email')}
                  />
                </div>

                <div>
                  <label className="block text-foreground font-medium mb-2">
                    {pageContent?.contact?.formLabels?.message || t('contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    placeholder={pageContent?.contact?.formLabels?.message || t('contact.form.message')}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 text-lg font-semibold"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {pageContent?.contact?.formLabels?.send || t('contact.form.send')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;